/**
 * Which Mod branch this window reads, per project (RFC 0313 3j).
 *
 * Picked from the chip on the project's row. It changes what the window reads
 * and writes, never what a session does: a session's branch is on its own log.
 * Kept in this window's storage, so two windows can look at two branches.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import { copyOnBranch, findBranch } from '@mod/core/model'
import { useEffect, useState, useSyncExternalStore } from 'react'
import { useDocument } from '../world/repo/useDocument'

const key = (projectId: string) => `mod_branch:${projectId}`
const listeners = new Set<() => void>()
const cache = new Map<string, string>()

function read(projectId: string): string {
  if (!cache.has(projectId)) {
    let v = ''
    try {
      v = localStorage.getItem(key(projectId)) ?? ''
    } catch {
      // No storage: main.
    }
    cache.set(projectId, v)
  }
  return cache.get(projectId) ?? ''
}

export function setProjectBranch(projectId: string, branch: string): void {
  cache.set(projectId, branch)
  try {
    if (branch) localStorage.setItem(key(projectId), branch)
    else localStorage.removeItem(key(projectId))
  } catch {
    // Held for this page only.
  }
  for (const fn of listeners) fn()
}

/** The open branch this window reads in a project, or '' for main. */
export function useProjectBranch(projectId: string | undefined): string {
  const picked = useSyncExternalStore(
    (fn) => {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
    () => (projectId ? read(projectId) : ''),
  )
  const [projectDoc] = useDocument(projectId as AnyDocumentId | undefined)
  // A merged or discarded branch is main again.
  if (!picked || findBranch(projectDoc, picked)?.status !== 'open') return ''
  return picked
}

/**
 * The document a pane on a branch edits: the branch's copy, made when the pane
 * opens it (the window copies on open; an agent copies on its first write).
 * Returns the id to load, or undefined while the copy is being made.
 */
export function useBranchDocument(
  projectId: string,
  documentId: string,
  enabled: boolean,
): { id: string | undefined; branch: string } {
  const repo = useRepo() as unknown as Repo
  const branch = useProjectBranch(enabled ? projectId : undefined)
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  const existing = branch
    ? findBranch(projectDoc, branch)?.copies[documentId]
    : undefined
  const [made, setMade] = useState<string | undefined>()
  useEffect(() => {
    setMade(undefined)
    if (!branch || existing) return
    let cancelled = false
    void copyOnBranch(repo, projectId, branch, documentId)
      .then((id) => {
        if (!cancelled) setMade(id)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [repo, projectId, branch, documentId, existing])
  if (!branch) return { id: documentId, branch: '' }
  return { id: existing ?? made, branch }
}

/**
 * An app view's copy on the window's branch, if the branch has written one.
 * Read only: opening an app does not copy its view; an edit to it does. A
 * branch that has not touched the view runs main's.
 */
export function useBranchCopyOf(
  projectId: string,
  documentId: string | undefined,
): string | undefined {
  const branch = useProjectBranch(projectId)
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  if (!branch || !documentId) return undefined
  return findBranch(projectDoc, branch)?.copies[documentId]
}
